export default {
  computed: {
    baseSystemStatuses () {
      return this.$store.getters.getSystemSetting('appointment_statuses')
    },
    baseInvoicedStatusColors () {
      return this.company.appointment_invoiced_status_colors || {}
    },
    basePaidStatusColors () {
      return this.company.appointment_paid_status_colors || {}
    }
  },
  methods: {
    baseStatusOptions (withAll = false, withUnscheduled = true) {
      const arr = []
      if (withAll) {
        arr.push({
          value: null,
          label: 'All Appointments',
          hex_color: '#000'
        })
      }
      if (withUnscheduled) {
        arr.push({
          value: 'UNSCHEDULED',
          label: this.baseFieldName('UNSCHEDULED'),
          hex_color: this.baseFieldColor('UNSCHEDULED')
        })
      }
      arr.push(...[
        {
          value: 'CANCELLED',
          label: this.baseFieldName('CANCELLED'),
          hex_color: this.baseFieldColor('CANCELLED')
        },
        {
          value: 'UNCONFIRMED',
          label: this.baseFieldName('UNCONFIRMED'),
          hex_color: this.baseFieldColor('UNCONFIRMED')
        },
        {
          value: 'SCHEDULED',
          label: this.baseFieldName('SCHEDULED'),
          hex_color: this.baseFieldColor('SCHEDULED')
        },
        {
          value: 'DEPARTED',
          label: this.baseFieldName('DEPARTED'),
          hex_color: this.baseFieldColor('DEPARTED')
        },
        {
          value: 'IN_PROGRESS',
          label: this.baseFieldName('IN_PROGRESS'),
          hex_color: this.baseFieldColor('IN_PROGRESS')
        },
        {
          value: 'COMPLETE',
          label: this.baseFieldName('COMPLETE'),
          hex_color: this.baseFieldColor('COMPLETE')
        },
        {
          value: 'DEPARTED_INCOMPLETE',
          label: this.baseFieldName('DEPARTED_INCOMPLETE'),
          hex_color: this.baseFieldColor('DEPARTED_INCOMPLETE')
        }
      ])
      return arr
    },
    baseStatusSettings (status) {
      return this.baseSystemStatuses[status]
    },
    baseFieldColor (status) {
      return this.company.appointment_status_colors[status] || '#757575'
    },
    /**
     * Get the effective field color based on status and invoiced state.
     * For COMPLETE and DEPARTED_INCOMPLETE statuses, returns invoiced-specific colors.
     * For other statuses, returns the base status color.
     *
     * @param {string} status - The appointment status
     * @param {boolean} isInvoiced - Whether the appointment is invoiced
     * @returns {string} The hex color
     */
    baseEffectiveFieldColor (status, isInvoiced) {
      // Only apply invoiced colors for completed statuses
      if (status === 'COMPLETE' || status === 'DEPARTED_INCOMPLETE') {
        const invoicedKey = `${status}_${isInvoiced ? 'INVOICED' : 'NOT_INVOICED'}`
        const invoicedColor = this.baseInvoicedStatusColors[invoicedKey]
        if (invoicedColor) {
          return invoicedColor
        }
      }
      // Fall back to base status color
      return this.baseFieldColor(status)
    },
    /**
     * Get the effective field color based on status, invoiced state, and paid state.
     * For COMPLETE and DEPARTED_INCOMPLETE statuses that are invoiced, returns paid-specific colors.
     * For non-invoiced completed statuses, returns invoiced-specific colors.
     * For other statuses, returns the base status color.
     *
     * @param {string} status - The appointment status
     * @param {boolean} isInvoiced - Whether the appointment is invoiced
     * @param {boolean} isPaid - Whether the appointment is paid
     * @returns {string} The hex color
     */
    baseEffectiveFieldColorWithPayment (status, isInvoiced, isPaid) {
      // Default colors for paid status when not configured
      const defaultPaidColors = {
        COMPLETE_PAID: '#2e7d32',
        COMPLETE_NOT_PAID: '#c62828',
        DEPARTED_INCOMPLETE_PAID: '#558b2f',
        DEPARTED_INCOMPLETE_NOT_PAID: '#b71c1c'
      }

      // Only apply paid colors for completed statuses that are invoiced
      if ((status === 'COMPLETE' || status === 'DEPARTED_INCOMPLETE') && isInvoiced) {
        const paidKey = `${status}_${isPaid ? 'PAID' : 'NOT_PAID'}`
        const paidColor = this.basePaidStatusColors[paidKey] || defaultPaidColors[paidKey]
        if (paidColor) {
          return paidColor
        }
      }
      // Fall back to invoiced color logic
      return this.baseEffectiveFieldColor(status, isInvoiced)
    },
    /**
     * Get the invoiced status options for the settings UI.
     * Returns array of status variants with labels and default colors.
     */
    baseInvoicedStatusOptions () {
      return [
        {
          value: 'COMPLETE_INVOICED',
          label: 'Completed (Invoiced)',
          status: 'COMPLETE'
        },
        {
          value: 'COMPLETE_NOT_INVOICED',
          label: 'Completed (Not Invoiced)',
          status: 'COMPLETE'
        },
        {
          value: 'DEPARTED_INCOMPLETE_INVOICED',
          label: 'Additional Work Required (Invoiced)',
          status: 'DEPARTED_INCOMPLETE'
        },
        {
          value: 'DEPARTED_INCOMPLETE_NOT_INVOICED',
          label: 'Additional Work Required (Not Invoiced)',
          status: 'DEPARTED_INCOMPLETE'
        }
      ]
    },
    /**
     * Get the paid status options for the settings UI.
     * Returns array of status variants with labels for paid/not paid states.
     * Only applicable for invoiced appointments.
     */
    basePaidStatusOptions () {
      return [
        {
          value: 'COMPLETE_PAID',
          label: 'Completed (Paid)',
          status: 'COMPLETE'
        },
        {
          value: 'COMPLETE_NOT_PAID',
          label: 'Completed (Not Paid)',
          status: 'COMPLETE'
        },
        {
          value: 'DEPARTED_INCOMPLETE_PAID',
          label: 'Additional Work Required (Paid)',
          status: 'DEPARTED_INCOMPLETE'
        },
        {
          value: 'DEPARTED_INCOMPLETE_NOT_PAID',
          label: 'Additional Work Required (Not Paid)',
          status: 'DEPARTED_INCOMPLETE'
        }
      ]
    },
    defaultRunsheetFilterState () {
      return ['SCHEDULED', 'IN_PROGRESS', 'DEPARTED']
    },
    defaultFilterState () {
      return ['UNCONFIRMED', 'SCHEDULED', 'IN_PROGRESS', 'CANCELLED', 'DEPARTED', 'COMPLETE', 'DEPARTED_INCOMPLETE']
    },
    baseFieldName (status) {
      return this.baseStatusSettings(status).name
    }
  }
}
